/* eslint-disable @typescript-eslint/no-empty-function */
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Cell, Pie, PieChart } from 'recharts'
import { Box, DashboardCard, P } from 'styling/GlobalStyles'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IWidget } from 'types/dashboard.interface'
import { TitleText } from 'styling/Typography'
import { WidgetEditOverlay } from 'pages/Dashboard/Dashboard.styled'
import { RootState } from 'store'

/** Fallback when `accommodationSummary` is missing (matches design mock) */
const FALLBACK_CAPACITY = 200
const FALLBACK_OCCUPIED = 164
const FALLBACK_AVAILABLE = 36

const OCCUPIED_GREEN = '#22c55e'
const AVAILABLE_CYAN = '#38bdf8'
const CHART_ORANGE = '#ff9900'

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const ManageLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colorOrange};
  cursor: pointer;

  &:disabled {
    cursor: inherit;
    opacity: 0.85;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  align-items: center;
  gap: 1rem;
`

const ChartWrap = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  flex-shrink: 0;
`

const CenterLabel = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  text-align: center;
`

const CenterPct = styled.span`
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme }) => theme.textColor};
`

const CenterSub = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  color: ${({ theme }) => theme.greyText};
  margin-top: 4px;
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 0.75rem;

  .meta-label {
    color: ${({ theme }) => theme.greyText};
    font-weight: 500;
  }
`

const StatCard = styled(Box)<{ $accent?: 'green' | 'blue' }>`
  border-radius: 10px;
  padding: 10px 8px 10px 11px;
  border-left: 3px solid transparent;
  ${(p) => p.$accent === 'green' && `border-left-color: ${OCCUPIED_GREEN};`}
  ${(p) => p.$accent === 'blue' && `border-left-color: ${AVAILABLE_CYAN};`}
`

const StatValue = styled.div<{ $variant?: 'default' | 'green' | 'cyan' }>`
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme, $variant }) => {
    if ($variant === 'green') {
      return OCCUPIED_GREEN
    }
    if ($variant === 'cyan') {
      return AVAILABLE_CYAN
    }
    return theme.textColor
  }};
`

const StatLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.greyText};
  margin-top: 6px;
`


export const HrAccommodationWidget = ({
  modalOpen,
  removeWidget,
  onDragOver,
  onDragStart,
  onDragDrop,
}: IWidget) => {
  const { accommodationSummary } = useSelector((state: RootState) => state.hrDashboard)
  const { darkMode } = useSelector((state: RootState) => state.globals)
  const navigate = useNavigate()

  const { totalCapacity, occupied, available, dirtyRooms, guestStays, occupancyPct, pieData } = useMemo(() => {
    const s = accommodationSummary
    const totalCapacity = s?.totalCapacity ?? FALLBACK_CAPACITY
    const occupied = s?.occupied ?? FALLBACK_OCCUPIED
    const available = s?.available ?? FALLBACK_AVAILABLE
    const dirtyRooms = s?.dirtyRooms ?? 0
    const guestStays = s?.guestStays ?? 0

    const pct =
      s?.occupancyRate != null
        ? Math.min(100, Math.max(0, Math.round(s.occupancyRate)))
        : totalCapacity > 0
          ? Math.min(100, Math.max(0, Math.round((occupied / totalCapacity) * 100)))
          : 0

    const trackColor = darkMode ? '#4a4a4a' : '#d4d4d4'

    const pieData =
      pct >= 100
        ? [{ name: 'full', value: 100, color: CHART_ORANGE }]
        : pct <= 0
          ? [{ name: 'track', value: 100, color: trackColor }]
          : [
              { name: 'occ', value: pct, color: CHART_ORANGE },
              { name: 'rem', value: 100 - pct, color: trackColor },
            ]

    return { totalCapacity, occupied, available, dirtyRooms, guestStays, occupancyPct: pct, pieData }
  }, [accommodationSummary, darkMode])

  return (
    <DashboardCard
      draggable={modalOpen}
      onDragStartCapture={() => {
        onDragStart('accommodation', 1)
      }}
      onDrop={onDragDrop}
      onDragEnd={(e) => {
        e.preventDefault()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        onDragOver('accommodation')
      }}
      width='1fr'
      style={{ gridArea: 'accommodation', cursor: modalOpen ? 'grab' : 'normal' }}
    >
      {modalOpen && (
        <WidgetEditOverlay
          onClick={(e) => {
            removeWidget('accommodation')
            e.stopPropagation()
          }}
        >
          <P>Remove Widget</P>
          <CloseIcon />
        </WidgetEditOverlay>
      )}
      <CardHeader>
        <TitleText weight='bold' style={{ margin: 0, flex: 1, minWidth: 0 }}>
          ACCOMMODATION
        </TitleText>
        <ManageLink
          type='button'
          disabled={modalOpen}
          onClick={(e) => {
            e.stopPropagation()
            if (!modalOpen) {
              navigate('/hr/accommodation')
            }
          }}
        >
          Manage
          <span aria-hidden='true'>{'>'}</span>
        </ManageLink>
      </CardHeader>
      <Content>
        <ChartWrap>
          <PieChart width={160} height={160} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
            <Pie
              data={pieData}
              dataKey='value'
              cx='50%'
              cy='50%'
              innerRadius={60}
              outerRadius={76}
              startAngle={90}
              endAngle={-270}
              paddingAngle={0}
              cornerRadius={8}
              stroke='none'
              isAnimationActive={false}
            >
              {pieData.map((entry, index) => (
                <Cell key={`${entry.name}-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
          <CenterLabel>
            <CenterPct>{occupancyPct}%</CenterPct>
            <CenterSub>OCCUPANCY</CenterSub>
          </CenterLabel>
        </ChartWrap>

        <StatGrid>
          <StatCard>
            <StatValue>{totalCapacity}</StatValue>
            <StatLabel>CAPACITY</StatLabel>
          </StatCard>
          <StatCard $accent='green'>
            <StatValue $variant='green'>{occupied}</StatValue>
            <StatLabel>OCCUPIED</StatLabel>
          </StatCard>
          <StatCard $accent='blue'>
            <StatValue $variant='cyan'>{available}</StatValue>
            <StatLabel>AVAILABLE</StatLabel>
          </StatCard>
        </StatGrid>
        <MetaRow>
          <span className='meta-label'>Dirty Rooms</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 700, color: CHART_ORANGE }}>{dirtyRooms}</span>
        </MetaRow>
        <MetaRow>
          <span className='meta-label'>Guest Stays</span>
          <StatValue $variant='cyan' style={{ fontSize: '0.875rem' }}>{guestStays}</StatValue>
        </MetaRow>

      </Content>
    </DashboardCard>
  )
}
